import {
  Card,
  CardClose,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '@ais-ui/utils';
import { PiArrowsClockwiseFill, PiArrowSquareOut } from 'react-icons/pi';
import { openPopupOnSecondaryScreen, PopupType } from '@ais-ui/popups';
import { PortDetailsPanel, PortIdentification } from './PortDetailsPanel';
import { PortActions } from './PortActions';
import { useDrawPort } from './useDrawPort';
import { usePermissions } from '../Permissions/PermissionsProvider';
import { MissingPermission } from '../../components/MissingPermission';
import { CENTERCOMPONENTS } from '../Map/MapPanel/CenterComponents';
import { useMapPanelContext } from '../Map/MapPanel/MapPanelContext';
import { useRoutes } from '../Routes/RoutesContext';
import { useSelectionState } from '../../components/SelectionStateProvider';

interface TargetDetailsMapProps {
  className?: string;
}

interface PortCardStackProps {
  className?: string;
}

const PortCardStack = ({ className }: PortCardStackProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { selectedPorts, setSelectedPorts, portSelectionMode } = useSelectionState();
  const goToNextCard = () => {
    if (currentIndex < selectedPorts.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const permissions = usePermissions();

  const handleOpenPopup = useCallback(() => {
    const currentPort = selectedPorts[currentIndex];
    if (!currentPort) return;
    
    const params: Record<string, string | number> = {
      portId: Number(currentPort.id),
      portName: currentPort.name,
      portLocode: currentPort.locode,
      initialTab: 'nextport',
    };
    
    openPopupOnSecondaryScreen(PopupType.Routes, params);
  }, [selectedPorts, currentIndex]);

  const multiplePortsSelected = useMemo(() => {
    return selectedPorts.length > 1;
  }, [selectedPorts]);

  const nextPortName = useMemo(() => {
    if (!multiplePortsSelected) {
      return '';
    }
    if (currentIndex < selectedPorts.length - 1) {
      const nextSelectedPort = selectedPorts[currentIndex + 1];
      return nextSelectedPort.name;
    } else {
      const nextSelectedPort = selectedPorts[0];
      return nextSelectedPort.name;
    }
  }, [currentIndex, multiplePortsSelected, selectedPorts]);

  const currentPort = useMemo(() => {
    if (selectedPorts.length > 0 && currentIndex < selectedPorts.length) {
      return selectedPorts[currentIndex];
    }
    return selectedPorts[0] || null;
  }, [currentIndex, selectedPorts]);

  useEffect(() => {
    if (selectedPorts.length > 0 && currentIndex >= selectedPorts.length) {
      setCurrentIndex(0);
    }
  }, [selectedPorts.length, currentIndex]);

  if (!currentPort) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative max-h-screen',
        className,
        selectedPorts.length === 2 && 'ml-3',
        selectedPorts.length >= 2 && 'ml-6',
      )}
    >
      <Card
        id="portinfo-card"
        className={cn(
          'relative flex flex-col min-w-[464px] max-w-[464px] h-screen z-10 shadow-lg',
        )}
        style={{ maxHeight: 'calc(100vh - 2rem)' }}
      >
        <CardHeader className="pb-2">
          <CardClose onClick={() => setSelectedPorts([])} />
          {portSelectionMode === 'nextport' && permissions.nextport && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="absolute right-12 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onClick={handleOpenPopup}
                >
                  <PiArrowSquareOut className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Open in new window</TooltipContent>
            </Tooltip>
          )}
          {multiplePortsSelected && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={cn(
                    "absolute top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                    portSelectionMode === 'nextport' && permissions.nextport ? "right-20" : "right-12"
                  )}
                  onClick={goToNextCard}
                >
                  <PiArrowsClockwiseFill className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {`Go to next port: ${nextPortName}`}
              </TooltipContent>
            </Tooltip>
          )}
          <CardTitle className="text-lg font-medium text-muted-semibold">
            {currentPort.name}
          </CardTitle>
        </CardHeader>

        <CardContent className="flex flex-col overflow-hidden space-y-6 h-full">
          <div className="grid gap-4">
            <PortIdentification port={currentPort}></PortIdentification>
          </div>
          {/* Show NextPort panel for 'nextport' mode */}
          {portSelectionMode === 'nextport' && permissions.nextport && (
            <PortDetailsPanel port={currentPort} />
          )}
          {portSelectionMode === 'nextport' && !permissions.nextport && (
            <MissingPermission
              entity={'nextport'}
              srcImage={'empty_list_bro.png'}
            />
          )}
          {/* 'routes' mode shows only routes table (handled in useEffect above) */}
        </CardContent>
        <CardFooter className="">
          <PortActions port={currentPort}></PortActions>
        </CardFooter>
      </Card>
      <div className="absolute top-0 left-0">
        {selectedPorts.length >= 3 && (
          <Card
            id="stack-card-2"
            className={cn(
              'absolute z-1 min-w-[464px] max-w-[464px] h-screen -top-3 -left-6 shadow-md cursor-default pointer-events-auto',
            )}
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
          ></Card>
        )}
        {selectedPorts.length >= 2 && (
          <Card
            id="stack-card-1"
            className={cn(
              'absolute z-2 min-w-[464px] max-w-[464px] h-screen -top-1.5 -left-3 shadow-md cursor-default pointer-events-auto',
            )}
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
          ></Card>
        )}
      </div>
    </div>
  );
};

export function PortDetails({ className }: TargetDetailsMapProps) {
  const { selectedPorts, setSelectedPorts, portSelectionMode } = useSelectionState();
  const { setCenterComponent } = useMapPanelContext();
  const { setPort } = useRoutes();
  const permissions = usePermissions();

  // Get the current port (first selected)
  const currentPort = selectedPorts.length > 0 ? selectedPorts[0] : null;

  // Always draw port on map when selected
  useDrawPort(currentPort);

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedPorts([]);
      }
    };

    window.addEventListener('keydown', onEscape);

    return () => {
      window.removeEventListener('keydown', onEscape);
    };
  }, [setSelectedPorts]);

  // Handle routes mode - show routes table without card
  useEffect(() => {
    if (!currentPort || !permissions.port || portSelectionMode !== 'routes') {
      return;
    }

    setPort(currentPort);
    setCenterComponent(CENTERCOMPONENTS.ROUTES);

    return () => {
      setCenterComponent(CENTERCOMPONENTS.NONE);
    };
  }, [currentPort, permissions.port, portSelectionMode, setCenterComponent, setPort]);

  // Don't show card for 'routes' mode - only show the routes table
  if (selectedPorts.length === 0 || portSelectionMode === 'routes') {
    return null;
  }

  return <PortCardStack className={className} />;
}
